import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { ApplicationDetail } from "@/lib/api/review";
import { ReadOnlyField, FileCard } from "./Shared";
import type { MakeUpdateFn, FieldStatus } from "./Shared";
import type { ApplicationStageStatus } from "@/lib/api/vendor";
import { cn } from "@/lib/utils";

// ─── Business Information section ─────────────────────────────────────────────

export function BusinessInformationContent({
  isWide,
  stageStatus,
  onMakeUpdate,
  updatedFields,
  application,
}: {
  isWide: boolean;
  stageStatus?: ApplicationStageStatus;
  onMakeUpdate?: MakeUpdateFn;
  updatedFields?: Set<string>;
  application?: ApplicationDetail;
}) {
  const { t } = useI18n();
  const data = application?.businessInformation;
  const fr = application?.fieldRejections;
  const fs = (key: string): FieldStatus | undefined => {
    if (updatedFields?.has(key)) return "updated";
    return fr?.[key]
      ? stageStatus === "update_required"
        ? "update_required"
        : "rejected"
      : undefined;
  };

  if (!data) return null;

  return (
    <View className={cn("items-start w-full", isWide ? "gap-0" : "px-page-x gap-4")}>
      {/* Row 1: Company Legal Name (Chinese) — full width on both layouts */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step2.companyNameChinese"
          value={data.companyNameChinese}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 2: Company Legal Name (English) + (Arabic) — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step2.companyNameEnglish"
          value={data.companyNameEnglish}
          flex={isWide}
          fieldStatus={fs("company_name_english")}
          onMakeUpdate={onMakeUpdate ? () => onMakeUpdate("company_name_english") : undefined}
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step2.companyNameArabic"
          value={data.companyNameArabic ?? null}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Section header: Headquarters Address */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-3" : "pt-1")}>
        <AppText weight="bold" className="text-base text-dark-text-15 uppercase tracking-widest">
          {t("preVetting.step2.sectionHqAddress")}
        </AppText>
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 3: Registered Office Address — full width on both */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <ReadOnlyField labelKey="preVetting.step2.hqAddress" value={data.registeredAddress} />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 4: City + Postal Code — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField labelKey="preVetting.step2.hqCity" value={data.city} flex={isWide} />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
        <ReadOnlyField
          labelKey="preVetting.step2.hqPostalCode"
          value={data.postalCode}
          flex={isWide}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 5: Unified Social Credit Code + Tax Registration Number — 2-col on desktop */}
      <View className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-2" : "")}>
        <ReadOnlyField
          labelKey="preVetting.step2.unifiedSocialCreditCode"
          value={data.ussc}
          flex={isWide}
          fieldStatus={fs("unified_social_credit_code")}
          onMakeUpdate={onMakeUpdate ? () => onMakeUpdate("unified_social_credit_code") : undefined}
        />
        {!isWide && <View className="border-b border-stroke-50 my-4 w-full" />}
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 6: Business License */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-2" : "")}>
        <AppText weight="medium" className="text-base text-supporting-text-40 pt-2">
          {t("preVetting.step2.businessLicense")}
        </AppText>
        {data.businessLicenseUrl != null ? (
          <FileCard
            url={data.businessLicenseUrl}
            fieldStatus={fs("business_license_document")}
            onMakeUpdate={
              onMakeUpdate ? () => onMakeUpdate("business_license_document") : undefined
            }
          />
        ) : (
          <View className="py-4">
            <AppText weight="medium" className="text-base text-dark-text-10">
              {t("vendor.application.detail.notUploaded")}
            </AppText>
          </View>
        )}
      </View>
    </View>
  );
}
